import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { createSlice } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';

export const truPromptApi = createApi({
    reducerPath: 'truPromptApi',
    baseQuery: fetchBaseQuery({ 
        baseUrl: `${process.env.REACT_APP_BACKEND_URL}/api`,
        prepareHeaders: (headers) => {
            const token = Cookies.get('token');
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            headers.set('Content-Type', 'application/json');
            return headers;
        },
    }),
    tagTypes: ['Prompts'],
    endpoints: (builder) => ({
        GetPrompts: builder.query({
            query: () => ({
                url: '/prompts/',
                method: 'GET'
            }),
            transformResponse: (response) => response.allPrompts,
            providesTags: ['Prompts'],
        }),
        getAuthPrompts: builder.query({
            query: () => ({
                url: '/prompts/auth/verifiedUserPrompts/',
                method: 'GET'
            }),
            transformResponse: (response) => response.allPrompts,
            providesTags: ['Prompts'],
        }),
        getSinglePrompt: builder.query({
            query: (id) => ({
                url: `/prompts/${id}`,
                method: 'GET'
            }),
            transformResponse: (response) => response.prompt,
            providesTags: (result, error, id) => [{ type: 'Prompts', id }]
        }),
        updatePromptCount: builder.mutation({
            query: (id) => ({
                url: `/prompts/updatePromptCount/${id}`,
                method: 'PATCH'
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Prompts', id }],
        }),
        postPrompt: builder.mutation({
            query: (newPrompt) => ({
                url: '/prompts/auth/verifiedUserPrompts',
                method: 'POST',
                body: newPrompt
            }),
            invalidatesTags: ['Prompts']
        }),
    }),
});

const promptSlice = createSlice({
    name: 'prompt',
    initialState: {
        promptId: null,
        selectedOptions: [],
        loading: false,
        currentPrompt: "",
        mainComponent: 'newChat',
        isDropdownOpen: false,
    },
    reducers: {
        setPromptId: (state, action) => {
            state.promptId = action.payload;
        },
        setSelectedOptions: (state, action) => {
            state.selectedOptions = action.payload;
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setCurrentPrompt: (state, action) => {
            state.currentPrompt = action.payload;
        },
        setMainComponent: (state, action) => {
            state.mainComponent = action.payload;
        },
        setIsDropdownOpen: (state, action) => {
            state.isDropdownOpen = action.payload;
        },
    },
});

// Export actions
export const {
    setPromptId,
    setSelectedOptions,
    setLoading,
    setCurrentPrompt,
    setMainComponent,
    setIsDropdownOpen
} = promptSlice.actions;

// Export selectors
export const selectPromptId = (state) => state.prompt.promptId;
export const Loading = (state) => state.prompt.loading;
export const CurrentPrompt = (state) => state.prompt.currentPrompt;
export const selectMainComponent = (state) => state.prompt.mainComponent;
export const selectIsDropdownOpen = (state) => state.prompt.isDropdownOpen;

// Export API hooks
export const {
    useGetAuthPromptsQuery,
    useGetPromptsQuery,
    useGetSinglePromptQuery,
    useUpdatePromptCountMutation,
    usePostPromptMutation
} = truPromptApi;

export const promptReducer = promptSlice.reducer;

const promptResultsSlice = createSlice({
    name: 'promptResults',
    initialState: {
        // promptSource: 'openAIPrompts', // To list all saved prompts in the local storage
        promptSource: 'singlePrompt', // Default source
        openAIResults: [],
    },
    reducers: {
        setPromptSource(state, action) {
            state.promptSource = action.payload; // Set the source to either 'openAI' or 'single'
        },
        clearResults(state) {
            state.promptSource = ''; // Reset to default
        },
        setOpenAIResults(state, action) { // Add this reducer
            state.openAIResults = action.payload; // Update openAIResults
        },
    },
});

export const { setPromptSource, clearResults, setOpenAIResults } = promptResultsSlice.actions;

export const selectPromptSource = (state) => state.promptResults.promptSource;
export const OpenAIResults = (state) => state.promptResults.openAIResults;
export const promptResultsReducer = promptResultsSlice.reducer;